import type {FocusEvent, KeyboardEvent} from 'react';
import {useCallback, useRef} from 'react';
import {tabbable} from 'tabbable';

import {useOnClickOutside} from '@pexip/hooks';

type SetTooltipVisibility = (options: {
    isVisible: boolean;
    isControlled?: boolean;
}) => void;

/**
 * Manages dismissal and logical focus order for a portalled notification
 * tooltip.
 *
 * @example
 * const interactions = useNotificationTooltipInteractions({
 *     setVisible,
 *     shouldCloseOnOutsideClick: true,
 * });
 */
export const useNotificationTooltipInteractions = ({
    setVisible,
    shouldCloseOnOutsideClick,
}: {
    setVisible: SetTooltipVisibility;
    shouldCloseOnOutsideClick: boolean;
}) => {
    const tabOrderGuardRef = useRef<HTMLSpanElement>(null);
    const tooltipStartGuardRef = useRef<HTMLSpanElement>(null);
    const tooltipEndGuardRef = useRef<HTMLSpanElement>(null);
    const targetRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);

    const close = useCallback(() => {
        if (
            document.activeElement instanceof HTMLElement &&
            overlayRef.current?.contains(document.activeElement)
        ) {
            const anchor = targetRef.current
                ? tabbable(targetRef.current).find(
                      element => element !== tabOrderGuardRef.current,
                  )
                : undefined;
            anchor?.focus();
        }

        setVisible({
            isVisible: false,
            isControlled: true,
        });
    }, [setVisible]);

    const handleOpenChange = useCallback(
        (isOpen: boolean) => {
            if (!isOpen) {
                close();
            }
        },
        [close],
    );

    const closeOnOutsideClick = useCallback(() => {
        shouldCloseOnOutsideClick && close();
    }, [shouldCloseOnOutsideClick, close]);

    const outsideClickRef = useOnClickOutside(closeOnOutsideClick);

    const focusRelativeToElement = useCallback(
        (element: HTMLElement | null, offset: -1 | 1) => {
            const pageElements = tabbable(document.body).filter(
                element => !element.closest('[data-portal-id]'),
            );
            if (pageElements.length === 0) {
                return;
            }

            const elementIndex = element ? pageElements.indexOf(element) : -1;
            if (elementIndex === -1) {
                const boundaryElement =
                    offset === 1 ? pageElements[0] : pageElements.at(-1);
                boundaryElement?.focus();
                return;
            }
            if (pageElements.length === 1) {
                return;
            }

            const adjacentIndex =
                (elementIndex + offset + pageElements.length) %
                pageElements.length;
            pageElements[adjacentIndex]?.focus();
        },
        [],
    );

    const focusRelativeToGuard = useCallback(
        (offset: -1 | 1) => {
            const guard = tabOrderGuardRef.current;
            if (!guard) {
                return;
            }
            focusRelativeToElement(guard, offset);
        },
        [focusRelativeToElement],
    );

    const getTooltipElements = useCallback(
        () =>
            overlayRef.current
                ? tabbable(overlayRef.current).filter(
                      element =>
                          element !== tooltipStartGuardRef.current &&
                          element !== tooltipEndGuardRef.current,
                  )
                : [],
        [],
    );

    const handleTooltipStartGuardFocus = useCallback(
        (event: FocusEvent<HTMLSpanElement>) => {
            focusRelativeToElement(
                event.relatedTarget instanceof HTMLElement
                    ? event.relatedTarget
                    : null,
                1,
            );
        },
        [focusRelativeToElement],
    );

    const handleTooltipEndGuardFocus = useCallback(
        (event: FocusEvent<HTMLSpanElement>) => {
            focusRelativeToElement(
                event.relatedTarget instanceof HTMLElement
                    ? event.relatedTarget
                    : null,
                -1,
            );
        },
        [focusRelativeToElement],
    );

    const handleGuardFocus = useCallback(
        (event: FocusEvent<HTMLSpanElement>) => {
            const tooltipElements = getTooltipElements();
            if (tooltipElements.length === 0) {
                const previousElement = event.relatedTarget;
                const movedForward =
                    previousElement instanceof Node &&
                    Boolean(
                        previousElement.compareDocumentPosition(
                            event.currentTarget,
                        ) & Node.DOCUMENT_POSITION_FOLLOWING,
                    );
                focusRelativeToGuard(movedForward ? 1 : -1);
                return;
            }

            const previousElement = event.relatedTarget;
            const movedForward =
                !(previousElement instanceof Node) ||
                Boolean(
                    previousElement.compareDocumentPosition(
                        event.currentTarget,
                    ) & Node.DOCUMENT_POSITION_FOLLOWING,
                );
            const focusTarget = movedForward
                ? tooltipElements[0]
                : tooltipElements.at(-1);
            focusTarget?.focus();
        },
        [focusRelativeToGuard, getTooltipElements],
    );

    const handleTooltipKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            if (event.key !== 'Tab' || !overlayRef.current) {
                return;
            }

            const tooltipElements = getTooltipElements();
            const focusedIndex = tooltipElements.indexOf(
                event.target as HTMLElement,
            );
            const isLeavingBackwards = event.shiftKey && focusedIndex === 0;
            const isLeavingForwards =
                !event.shiftKey && focusedIndex === tooltipElements.length - 1;

            if (!isLeavingBackwards && !isLeavingForwards) {
                return;
            }

            event.preventDefault();
            focusRelativeToGuard(isLeavingBackwards ? -1 : 1);
        },
        [focusRelativeToGuard, getTooltipElements],
    );

    return {
        close,
        handleGuardFocus,
        handleOpenChange,
        handleTooltipEndGuardFocus,
        handleTooltipKeyDown,
        handleTooltipStartGuardFocus,
        outsideClickRef,
        overlayRef,
        tabOrderGuardRef,
        targetRef,
        tooltipEndGuardRef,
        tooltipStartGuardRef,
    };
};
